import React, { useState } from 'react';
import Markdown from 'react-markdown';
import { Image as ImageIcon, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import type { IdeaChatMessage as IdeaChatMessageType } from '@core/types/slot-screen-generator.types';

interface IdeaChatMessageProps {
  message: IdeaChatMessageType;
  onImageClick?: (url: string) => void;
  isStreaming?: boolean;
}

export function IdeaChatMessage({ message, onImageClick, isStreaming }: IdeaChatMessageProps) {
  const isUser = message.role === 'user';
  const [showThinking, setShowThinking] = useState(false);
  const time = new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const hasThought = !!message.thoughtText;
  const isThinking = isStreaming && hasThought && !message.text;

  return (
    <div className={`flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}>
      {/* Role + time + mode badge */}
      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
        <span className={isUser ? '' : 'text-blue-400/70'}>{isUser ? 'You' : 'Gemini'}</span>
        {isUser && message.mode === 'create-image' && (
          <span className="flex items-center gap-0.5 text-yellow-500/70">
            <ImageIcon size={9} />
            image
          </span>
        )}
        <span>{time}</span>
      </div>

      {/* Thinking section (model messages only) */}
      {!isUser && hasThought && (
        <div className="max-w-[85%]">
          {isThinking ? (
            /* Active thinking: show animated indicator + streaming thought content */
            <div className="mb-1">
              <div className="flex items-center gap-1.5 text-xs text-purple-400/80 mb-1">
                <Sparkles size={12} className="animate-pulse" />
                <span className="italic">Thinking...</span>
              </div>
              <div className="border-l-2 border-purple-500/30 pl-3 text-xs text-zinc-500 italic leading-relaxed max-h-60 overflow-y-auto">
                <div className="idea-chat-markdown">
                  <Markdown>{message.thoughtText!}</Markdown>
                </div>
              </div>
            </div>
          ) : (
            /* Completed thinking: collapsible toggle */
            <button
              onClick={() => setShowThinking(prev => !prev)}
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-400 transition-colors mb-1"
            >
              <Sparkles size={11} className="text-purple-400/60" />
              <span>{showThinking ? 'Hide thinking' : 'Show thinking'}</span>
              {showThinking ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          )}
          {/* Expanded thought content (only when toggle is open and not actively thinking) */}
          {showThinking && !isThinking && (
            <div className="border-l-2 border-purple-500/30 pl-3 mb-2 text-xs text-zinc-500 italic leading-relaxed max-h-60 overflow-y-auto">
              <div className="idea-chat-markdown">
                <Markdown>{message.thoughtText!}</Markdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Text bubble */}
      {message.text && (
        <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
          isUser
            ? 'bg-zinc-700 text-zinc-200'
            : 'bg-zinc-800/50 text-zinc-300'
        }`}>
          {isUser ? (
            // User messages: plain text with newlines
            message.text.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))
          ) : (
            // Model messages: full markdown rendering
            <div className="idea-chat-markdown">
              <Markdown>{message.text}</Markdown>
            </div>
          )}
        </div>
      )}

      {/* Images */}
      {message.images.length > 0 && (
        <div className={`flex flex-wrap gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
          {message.images.map((url, i) => (
            url ? (
              <div
                key={i}
                className="relative rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500/50 transition-all"
                onClick={() => onImageClick?.(url)}
              >
                <img
                  src={url}
                  alt={`${isUser ? 'Reference' : 'Generated'} ${i + 1}`}
                  className={`object-cover rounded-lg ${
                    isUser
                      ? 'w-16 h-16'
                      : 'w-96 h-auto max-h-[36rem]'
                  }`}
                />
              </div>
            ) : null
          ))}
        </div>
      )}
    </div>
  );
}
